import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useNavigate, useParams } from "react-router";
import ProductService from "@/services/product";
import { useCart } from "contexts/cart";
import { useApp } from "contexts/app";
import { useAuth } from "contexts/auth";
import { useChat } from "contexts/chat";
import AnalyticService from "services/analytic";

const analyticService = new AnalyticService();

const useProductViewModel = () => {
    const { id } = useParams();
    const { showAddToCart, setPageTitle } = useApp();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [selectedVariant, setSelectedVariant] = useState();
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [itemQuantity, setItemQuantity] = useState(1);
    const [productOptions, setProductOptions] = useState([]);
    const [images, setImages] = useState();
    const [headingTitle, setHeadingTitle] = useState("");
    const [itemCaculatedPrice, setItemCaculatedPrice] = useState(0);
    const [ytPlayer, setYtPlayer] = useState({ open: false, url: "", id: "" });
    const [slickCurrentIndex, setSlickCurrentIndex] = useState(0);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [showShare, setShowShare] = useState(false);

    const { user } = useAuth();
    const { openChat, hasChatEntry } = useChat();
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const service = new ProductService();
    const sliderRef = useRef(null);

    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        height: 50,
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [id]);

    useEffect(() => {
        load();
    }, [id, user?.id]);

    useEffect(() => {
        if (selectedVariant) {
            setHeadingTitle(selectedVariant.variant_title);
        } else {
            setHeadingTitle(data.name);
        }
    }, [selectedVariant]);

    useEffect(() => {
        setPageTitle(data?.name || "Sản phẩm");
    }, [setPageTitle, data?.name]);

    useEffect(() => {
        calculateItemPrice();
    }, [selectedVariant, itemQuantity]);

    const calculateItemPrice = () => {
        if (!selectedVariant) {
            setItemCaculatedPrice(0);
            return;
        }

        const priceList = _.sortBy(selectedVariant.price_list, v => v.min * -1);
        for (let index = 0; index < priceList.length; index++) {
            const price = priceList[index];
            if (itemQuantity >= price.min) {
                setItemCaculatedPrice(price.price);
                break;
            }
        }
    };

    const loadRelated = async () => {
        const data = await service.getRelated(id);
        let data2 = _.shuffle(data);
        data2 = _.take(data2, 15);
        setRelatedProducts(data2);
    };

    const load = async () => {
        const data = await service.get(id);
        setData(data);
        setProductOptions(data?.options ?? []);
        setLoading(false);
        setSelectedOptions(data?.options.map(() => null));

        const getMediaKey = (item) => {
            if (item.type === "video") return `video:${item.id || item.video_url || item.url}`;
            return `image:${item.id || item.url || item.md_url || item.lg_url || item.sm_url}`;
        };

        const mediaMap = new Map();
        const addOrMergeMedia = (item) => {
            const key = getMediaKey(item);
            if (!key || key.endsWith(":undefined")) return;

            const existed = mediaMap.get(key);
            if (!existed) {
                mediaMap.set(key, { ...item });
                return;
            }

            const mergedVariantIds = _.uniq([...(existed.variant_ids || []), ...(item.variant_ids || [])]);
            const mergedVariantTitles = _.uniq([...(existed.variant_titles || []), ...(item.variant_titles || [])]);

            mediaMap.set(key, {
                ...existed,
                variant_ids: mergedVariantIds,
                variant_titles: mergedVariantTitles,
                variant: existed.variant || item.variant,
                variant_id: existed.variant_id || item.variant_id,
            });
        };

        const productImage = (data.images || []).map(x => ({ ...x, image_of: "product" }));
        productImage.forEach(addOrMergeMedia);

        const productVideos = (data.videos ?? []).map(x => ({ ...x, url: `https://img.youtube.com/vi/${x.id}/hqdefault.jpg`, image_of: "product", type: "video", video_url: x.url }));
        productVideos.forEach(addOrMergeMedia);

        for (let index = 0; index < data.variants.length; index++) {
            const variant = data.variants[index];
            const variantImages = (variant.images && variant.images.length > 0)
                ? variant.images
                : (variant.image ? [variant.image] : []);

            variantImages.forEach(img => {
                addOrMergeMedia({
                    ...img,
                    image_of: "variant",
                    variant_id: variant.variant_id,
                    variant: variant,
                    variant_ids: [variant.variant_id],
                    variant_titles: [variant.variant_title],
                });
            });
        }

        const opt0 = data?.options?.[0];
        const opt1 = data?.options?.[1];
        const getSelectedOrder = (opt, value) => {
            if (!opt?.values?.length) return Number.MAX_SAFE_INTEGER;
            const idx = opt.values.findIndex(v => v === value);
            return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        };

        const sortedVariants = [...(data?.variants || [])].sort((a, b) => {
            const a0 = a?.options?.find(o => o.id === opt0?.id)?.value;
            const b0 = b?.options?.find(o => o.id === opt0?.id)?.value;
            const byOpt0 = getSelectedOrder(opt0, a0) - getSelectedOrder(opt0, b0);
            if (byOpt0 !== 0) return byOpt0;

            const a1 = a?.options?.find(o => o.id === opt1?.id)?.value;
            const b1 = b?.options?.find(o => o.id === opt1?.id)?.value;
            const byOpt1 = getSelectedOrder(opt1, a1) - getSelectedOrder(opt1, b1);
            if (byOpt1 !== 0) return byOpt1;

            return String(a.variant_id || "").localeCompare(String(b.variant_id || ""));
        });

        const variantOrderMap = new Map(sortedVariants.map((variant, idx) => [variant.variant_id, idx]));
        const getVariantMediaOrder = (item) => {
            const ids = item.variant_ids || (item.variant_id ? [item.variant_id] : []);
            if (ids.length === 0) return Number.MAX_SAFE_INTEGER;
            return Math.min(...ids.map(id => variantOrderMap.get(id) ?? Number.MAX_SAFE_INTEGER));
        };

        let imageList = Array.from(mediaMap.values()).sort((a, b) => {
            const getMediaRank = (item) => {
                if (item.image_of === "product" && item.type !== "video") return 0;
                if (item.image_of === "product" && item.type === "video") return 1;
                return 2;
            };

            const byRank = getMediaRank(a) - getMediaRank(b);
            if (byRank !== 0) return byRank;

            if (getMediaRank(a) === 2) {
                const byVariantOrder = getVariantMediaOrder(a) - getVariantMediaOrder(b);
                if (byVariantOrder !== 0) return byVariantOrder;
            }

            return String(a.id || a.url || a.md_url || a.lg_url || "").localeCompare(
                String(b.id || b.url || b.md_url || b.lg_url || "")
            );
        });

        if (data.variants.length === 1) {
            setSelectedVariant(data.variants[0]);
        }

        if (imageList.length === 1) {
            imageList = [...imageList, imageList[0]];
        }

        setImages(imageList);
        setHeadingTitle(data.name);
        setSlickCurrentIndex(0);

        loadRelated();
        if (data) {
            analyticService.track({ event: "product_view", product_id: data.id });
        }
    };

    const goToSlide = (index) => {
        if (typeof index !== "number" || index < 0) {
            return;
        }
        setSlickCurrentIndex(index);
        requestAnimationFrame(() => {
            sliderRef.current?.slickGoTo(index);
        });
    };

    const findVariantBySelections = (nextSelectedOptions = []) => {
        const optionPairs = (data?.options || []).map((optionItem, optionIndex) => ({
            optionID: String(optionItem?.id || ""),
            valueID: String(nextSelectedOptions?.[optionIndex]?.id || ""),
        }));

        if (!optionPairs.length || optionPairs.some((pair) => !pair.optionID || !pair.valueID)) {
            return null;
        }

        const byOptionPairs = (data?.variants || []).find((variantItem) => {
            const variantOptions = Array.isArray(variantItem?.options) ? variantItem.options : [];
            return optionPairs.every((pair) => {
                const matched = variantOptions.find((option) => String(option?.id || "") === pair.optionID);
                return String(matched?.value || "") === pair.valueID;
            });
        });

        if (byOptionPairs) {
            return byOptionPairs;
        }

        const fallbackVariantID = optionPairs.map((pair) => pair.valueID).join("_");
        return (data?.variants || []).find((variantItem) => variantItem?.variant_id === fallbackVariantID) || null;
    };

    const onSelectOption = (option, index, focusSlide) => {
        const newSelect = [...selectedOptions];
        newSelect[index] = option;
        setSelectedOptions(newSelect);

        const variant = findVariantBySelections(newSelect);
        const variantID = variant?.variant_id || newSelect.map(x => x?.id).join("_");
        if (variant) {
            setSelectedVariant(variant);
        } else {
            setSelectedVariant();
        }

        if (focusSlide) {
            const firstOptionId = data?.options?.[0]?.id;
            const firstSelectedValue = newSelect?.[0]?.id;
            let slideIndex = -1;

            if (firstOptionId && firstSelectedValue) {
                const firstAttrVariantIds = (data.variants || [])
                    .filter(v => v.options?.find(o => o.id === firstOptionId)?.value === firstSelectedValue)
                    .map(v => v.variant_id);

                slideIndex = (images || []).findIndex(img => {
                    const variantIds = img.variant_ids || (img.variant?.variant_id ? [img.variant.variant_id] : []);
                    return variantIds.some(id => firstAttrVariantIds.includes(id));
                });
            }

            if (slideIndex < 0) {
                slideIndex = (images || []).findIndex(x => x.variant_ids?.includes(variantID) || x.variant?.variant_id === variantID);
            }

            if (slideIndex >= 0) {
                goToSlide(slideIndex);
            }
        }
    };

    const onSlideChange = (i) => {
        setSlickCurrentIndex(i);
    };

    return {
        id,
        loading,
        data,
        selectedVariant,
        selectedOptions,
        productOptions,
        images,
        headingTitle,
        itemCaculatedPrice,
        ytPlayer,
        slickCurrentIndex,
        relatedProducts,
        showShare,
        settings,
        sliderRef,
        hasChatEntry,
        openChat,
        navigate,
        showAddToCart,
        onSelectOption,
        onSlideChange,
        goToSlide,
        setYtPlayer,
        setShowShare,
    };
};

export default useProductViewModel;
